/**
 * @fileoverview Public landing page for unauthenticated users.
 * Redirects authenticated users to the dashboard.
 * @module features/landing/pages/LandingPage
 */

import { ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authService } from '@/lib/authService'
import { Spotlight } from '@/components/ui/spotlight-new'
import { LandingNavbar } from '../components/LandingNavbar'
import { HeroSection } from '../components/HeroSection'
import { IntegrationsSection } from '../components/IntegrationsSection'
import { CostBreakdownSection } from '../components/CostBreakdownSection'
import { ProfitChartSection } from '../components/ProfitChartSection'
import { PerformanceTableSection } from '../components/PerformanceTableSection'
import { PaymentsSection } from '../components/PaymentsSection'
import { EarlyAccessSection } from '../components/EarlyAccessSection'
import { FooterSection } from '../components/FooterSection'
import { Separator } from '@/components/ui/separator'
import { ScrollReveal } from '@/shared/components/ui'
import PageMeta from '@/shared/components/common/PageMeta'
import { generateLandingHreflang, type SupportedLanguage } from '@/shared/components/common/seoUtils'

interface LandingPageProps {
  /** Language for this landing page instance */
  lang?: SupportedLanguage
}

/**
 * Landing page component.
 * Redirects authenticated users to the dashboard.
 *
 * @component
 */
export function LandingPage({ lang = 'en' }: LandingPageProps): ReactElement {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('features-landing')

  // Set language based on URL
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  return (
    <div className="relative min-h-svh overflow-x-hidden bg-[oklch(0.15_0_200)]">
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={`/${lang}/`}
        lang={lang}
        keywords={[
          'e-commerce analytics',
          'profitability tracking',
          'margin analysis',
          'cost tracking',
          'ad spend',
          'BaseLinker integration',
        ]}
        hreflang={generateLandingHreflang(lang)}
      />

      {/* Background Effects */}
      <div className="fixed inset-0 dot-pattern-subtle pointer-events-none" />

      {/* Spotlight Effect - covers navbar and hero */}
      <div className="fixed inset-0 pointer-events-none">
        <Spotlight
          gradientFirst="radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(210, 100%, 85%, .04) 0, hsla(210, 100%, 55%, .01) 50%, hsla(210, 100%, 45%, 0) 80%)"
          gradientSecond="radial-gradient(50% 50% at 50% 50%, hsla(210, 100%, 85%, .03) 0, hsla(210, 100%, 55%, .01) 80%, transparent 100%)"
          gradientThird="radial-gradient(50% 50% at 50% 50%, hsla(210, 100%, 85%, .02) 0, hsla(210, 100%, 45%, .01) 80%, transparent 100%)"
        />
      </div>

      {/* Navigation */}
      <LandingNavbar />

      {/* Main Content */}
      <main className="pt-20">
        <ScrollReveal blur>
          <HeroSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <IntegrationsSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <CostBreakdownSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <ProfitChartSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <PerformanceTableSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <PaymentsSection />
        </ScrollReveal>
        <Separator />
        <ScrollReveal delay={0.1} blur>
          <EarlyAccessSection />
        </ScrollReveal>
        <FooterSection />
      </main>
    </div>
  )
}
